import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Tag, Share2, Store, Navigation, Globe, Phone, ExternalLink, Ticket } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { Overline, OfferCard, DealCountdown, offerEnd, img } from "@/components/Cards";
import { toast } from "sonner";

export default function OfferDetail() {
  const { slug } = useParams();
  const [o, setO] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setO(null); setNotFound(false);
    api.get(`/offers/${slug}`).then((r) => setO(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-neutral-400">Deal not found. <Link to="/offers" className="text-[#FFEA00]">Back to deals</Link></div>;
  if (!o) return <div className="py-32 text-center text-neutral-500">Loading...</div>;

  const b = o.business;
  const mapsUrl = b?.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.address)}` : null;

  const share = async () => {
    const url = window.location.href;
    const payload = { title: `${o.title} — Visit RiNo`, text: o.description?.slice(0, 120), url };
    if (navigator.share) { try { await navigator.share(payload); } catch {} }
    else { navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Offer",
    name: o.title,
    description: o.description,
    ...(o.image ? { image: [o.image] } : {}),
    ...(o.valid_until ? { availabilityEnds: o.valid_until } : {}),
    seller: { "@type": "Organization", name: o.business_name || "Visit RiNo" },
    url: `https://visitrino.com/offers/${o.slug}`,
  };

  return (
    <div data-testid="offer-detail">
      <Seo title={`${o.title} — RiNo Deal`} description={o.description?.slice(0, 155)} image={o.image} type="article" jsonLd={jsonLd} canonicalPath={`/offers/${o.slug}`} />

      <div className="relative h-[42vh] overflow-hidden grain">
        <img src={img(o.image)} alt={o.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-8">
            <Link to="/offers" className="flex w-fit items-center gap-2 text-neutral-300 hover:text-white text-sm mb-4" data-testid="back-to-offers"><ArrowLeft size={16} /> Deals</Link>
            <div><Overline>Deal</Overline></div>
            <h1 className="font-display font-black text-4xl sm:text-6xl text-white mt-2" data-testid="offer-title">{o.title}</h1>
            {o.discount && <span className="inline-flex items-center gap-2 bg-[#FFEA00] text-black font-bold px-4 py-1.5 mt-3 text-sm"><Tag size={15} /> {o.discount}</span>}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display font-bold text-2xl text-white mb-3">The Deal</h2>
          <p className="text-neutral-300 text-lg leading-relaxed whitespace-pre-line" data-testid="offer-description">{o.description || "Show up and enjoy this RiNo special."}</p>
          {o.fine_print && <p className="text-neutral-500 text-sm mt-4 border-l-2 border-white/10 pl-4">{o.fine_print}</p>}

          {b && (
            <div className="mt-10">
              <h2 className="font-display font-bold text-2xl text-white mb-4">Where to redeem</h2>
              <Link to={`/directory/${b.slug}`} data-testid="offer-venue-link"
                className="flex items-center gap-4 border border-white/10 bg-[#141414] p-4 hover:border-[#00E5FF] transition-colors group">
                <div className="w-20 h-20 flex-shrink-0 overflow-hidden">
                  <img src={img(b.image)} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div>
                  <div className="text-[#00E5FF] text-xs font-accent flex items-center gap-1"><Store size={13} /> {b.category}</div>
                  <div className="text-white font-display font-bold text-lg mt-0.5 group-hover:text-[#00E5FF] transition-colors">{b.name}</div>
                  {b.address && <div className="text-neutral-500 text-sm mt-0.5">{b.address}</div>}
                </div>
              </Link>
            </div>
          )}

          {o.more_offers?.length > 0 && (
            <div className="mt-12">
              <h2 className="font-display font-bold text-2xl text-white mb-4">More RiNo deals</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="more-offers">
                {o.more_offers.map((d, i) => <OfferCard key={d.id} o={d} index={i} />)}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-32 border border-white/10 bg-[#141414] p-6 space-y-4">
            {offerEnd(o) && (
              <div className="border border-[#FF0055]/30 bg-[#FF0055]/5 p-4">
                <div className="text-neutral-400 text-xs font-accent uppercase tracking-wider mb-2 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#FF0055] animate-pulse" /> Ending in</div>
                <DealCountdown end={offerEnd(o)} variant="full" />
              </div>
            )}
            {o.discount && (
              <div className="text-center border border-[#FFEA00]/30 bg-[#FFEA00]/5 py-4">
                <div className="font-display font-black text-2xl text-[#FFEA00]">{o.discount}</div>
                {o.valid_until && <div className="text-neutral-400 text-xs mt-1">Valid until {o.valid_until}</div>}
              </div>
            )}
            {o.redeem_url && (
              <a href={o.redeem_url} target="_blank" rel="noopener noreferrer" data-testid="offer-redeem-btn"
                className="w-full py-3 bg-[#FFEA00] hover:bg-[#fff04d] text-black text-sm font-bold flex items-center justify-center gap-2 transition-colors">
                <Ticket size={16} /> Redeem / Details
              </a>
            )}
            {b?.website && (
              <a href={b.website} target="_blank" rel="noopener noreferrer" data-testid="offer-website-btn"
                className="w-full py-3 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors">
                <Globe size={16} /> Visit Website
              </a>
            )}
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="offer-directions-btn"
                className="w-full py-3 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors">
                <Navigation size={16} /> Get Directions
              </a>
            )}
            {b?.phone && (
              <a href={`tel:${b.phone}`} className="w-full py-3 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors">
                <Phone size={16} /> {b.phone}
              </a>
            )}
            <button onClick={share} data-testid="offer-share-btn"
              className="w-full py-3 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors">
              <Share2 size={16} /> Share
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
